/**
 * ImportDictionaryDialog — upload a vendor data dictionary (Excel) for a
 * relational connector, preview the coverage, then start the automatic
 * completion pipeline on the BE.
 *
 * One dialog, three steps driven by a local state machine (no routing):
 *
 *   1. ``pick``    — choose the ``.xlsx``, optional company prefix, two
 *                    pipeline toggles. CTA "Anteprima" fires ``dry_run=true``.
 *   2. ``preview`` — coverage stats (tables / columns matched), the objects
 *                    the dictionary does not cover, the tables with the most
 *                    undocumented columns. CTA "Importa" fires ``dry_run=false``.
 *   3. ``started`` — the BE answered 202; the pipeline runs in the background
 *                    and its progress is visible in the Schema tab through the
 *                    existing profiling-status poll.
 *
 * Imported descriptions are marked ``user_edited`` on the BE, so a later AI
 * profiling never overwrites them — the reassurance line in the preview says
 * exactly that. Everything resets on close so the next open starts clean.
 */

import { Trans, useLingui } from "@lingui/react/macro";
import { BookOpenText, CheckCircle2, FileSpreadsheet, Loader2, ShieldCheck } from "lucide-react";
import * as React from "react";

import type { ImportDictionaryPreview } from "@ando/core/api";
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, cn } from "@ando/ui";

import { useImportDictionary, usePreviewDictionary } from "../../hooks/useImportDictionary";
import { InlineErrorBanner } from "../InlineErrorBanner";

export interface ImportDictionaryDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly connId: string;
}

type Step = "pick" | "preview" | "started";

const FILE_INPUT_ID = "import-dictionary-file";
const PREFIX_INPUT_ID = "import-dictionary-prefix";
const UNDOCUMENTED_TOP_N = 10;

function pct(part: number, whole: number): number {
  return whole > 0 ? Math.min(100, Math.round((part / whole) * 100)) : 0;
}

function isXlsx(file: File): boolean {
  return file.name.toLowerCase().endsWith(".xlsx");
}

const inputClass = cn(
  "w-full rounded-md border border-hairline bg-white/60 px-2.5 py-1.5 text-[12.5px] text-ink-1",
  "placeholder:text-ink-3",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
  "disabled:bg-ink-3/[0.04]",
);

export const ImportDictionaryDialog: React.FC<ImportDictionaryDialogProps> = ({
  open,
  onOpenChange,
  connId,
}) => {
  const { t } = useLingui();
  const previewMutation = usePreviewDictionary();
  const importMutation = useImportDictionary();
  // ``reset`` is referentially stable in TanStack v5, so the close-reset
  // effect can list it without re-running on every render.
  const { reset: resetPreview } = previewMutation;
  const { reset: resetImport } = importMutation;

  const [step, setStep] = React.useState<Step>("pick");
  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [prefix, setPrefix] = React.useState("");
  const [collectValues, setCollectValues] = React.useState(true);
  const [profileUncovered, setProfileUncovered] = React.useState(true);
  const [preview, setPreview] = React.useState<ImportDictionaryPreview | null>(null);

  // Reset on every close so the next open starts from "pick" with no
  // stale file, preview or error.
  React.useEffect(() => {
    if (open) return;
    setStep("pick");
    setFile(null);
    setFileError(null);
    setPrefix("");
    setCollectValues(true);
    setProfileUncovered(true);
    setPreview(null);
    resetPreview();
    resetImport();
  }, [open, resetPreview, resetImport]);

  const opts = React.useMemo(
    () => ({
      prefix: prefix.trim() || undefined,
      collectValues,
      profileUncovered,
    }),
    [prefix, collectValues, profileUncovered],
  );

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    resetPreview();
    if (picked && !isXlsx(picked)) {
      setFile(null);
      setFileError(t`Il file deve essere un Excel .xlsx.`);
      return;
    }
    setFileError(null);
    setFile(picked);
  };

  const handlePreview = () => {
    if (!file) return;
    previewMutation.mutate(
      { connId, file, opts },
      {
        onSuccess: (data) => {
          setPreview(data);
          setStep("preview");
        },
      },
    );
  };

  const handleImport = () => {
    if (!file) return;
    importMutation.mutate({ connId, file, opts }, { onSuccess: () => setStep("started") });
  };

  const handleBack = () => {
    resetImport();
    setStep("pick");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ariaTitle={t`Importa dizionario dati`}
        className="w-[min(600px,94vw)] max-w-none gap-4 bg-cream"
      >
        {step === "pick" ? (
          <>
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ando-orange/15">
                <BookOpenText className="h-5 w-5 text-ando-orange" aria-hidden />
              </span>
              <div className="flex flex-col gap-1">
                <DialogTitle className="text-[17px] leading-tight">
                  <Trans>Importa dizionario dati</Trans>
                </DialogTitle>
                <DialogDescription className="text-[13px] text-ink-2">
                  <Trans>
                    Carica il dizionario dati del fornitore in formato Excel: le descrizioni di
                    tabelle e colonne vengono importate senza AI, poi il completamento automatico
                    si occupa del resto.
                  </Trans>
                </DialogDescription>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <input
                id={FILE_INPUT_ID}
                name="file"
                type="file"
                accept=".xlsx"
                className="sr-only"
                onChange={onFileChange}
                disabled={previewMutation.isPending}
                data-testid="dictionary-file-input"
              />
              <label
                htmlFor={FILE_INPUT_ID}
                className={cn(
                  "flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-6 transition-all",
                  file
                    ? "border-ando-orange/50 bg-ando-orange-soft/20"
                    : "border-hairline bg-white/40 hover:border-ando-orange/40 hover:bg-white/70",
                )}
              >
                <FileSpreadsheet
                  className={cn("h-7 w-7", file ? "text-ando-orange" : "text-ink-3")}
                  aria-hidden
                />
                {file ? (
                  <div className="text-center">
                    <p data-testid="picked-file-name" className="font-medium text-[13px] text-ink-1">
                      {file.name}
                    </p>
                    <p className="mt-0.5 text-[11px] text-ink-3">
                      <Trans>Clicca per cambiare file</Trans>
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="font-medium text-[13px] text-ink-1">
                      <Trans>Seleziona il dizionario dati</Trans>
                    </p>
                    <p className="mt-0.5 text-[11.5px] text-ink-3">
                      <Trans>Formato supportato: .xlsx</Trans>
                    </p>
                  </div>
                )}
              </label>
              <InlineErrorBanner message={fileError} className="mt-1" />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor={PREFIX_INPUT_ID} className="font-medium text-[11.5px] text-ink-2">
                <Trans>Prefisso azienda</Trans>
              </label>
              <input
                id={PREFIX_INPUT_ID}
                name="prefix"
                type="text"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                placeholder="FOM_"
                autoComplete="off"
                spellCheck={false}
                disabled={previewMutation.isPending}
                className={inputClass}
              />
              <p className="text-[11.5px] text-ink-3">
                <Trans>Lasciare vuoto per rilevarlo automaticamente.</Trans>
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="flex cursor-pointer items-start gap-2 text-[12.5px] text-ink-1">
                <input
                  type="checkbox"
                  checked={collectValues}
                  onChange={(e) => setCollectValues(e.target.checked)}
                  disabled={previewMutation.isPending}
                  className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-ando-orange"
                />
                <span>
                  <Trans>Raccogli i valori dei codici (senza AI)</Trans>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 text-[12.5px] text-ink-1">
                <input
                  type="checkbox"
                  checked={profileUncovered}
                  onChange={(e) => setProfileUncovered(e.target.checked)}
                  disabled={previewMutation.isPending}
                  className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-ando-orange"
                />
                <span>
                  <Trans>Profila con l'AI solo le tabelle non coperte dal dizionario</Trans>
                </span>
              </label>
            </div>

            <InlineErrorBanner message={previewMutation.error?.message ?? null} className="mt-0" />

            <div className="mt-1 flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                <Trans>Annulla</Trans>
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handlePreview}
                disabled={!file || previewMutation.isPending}
              >
                {previewMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : null}
                <Trans>Anteprima</Trans>
              </Button>
            </div>
          </>
        ) : null}

        {step === "preview" && preview ? (
          <PreviewStep
            preview={preview}
            fileName={file?.name ?? ""}
            pending={importMutation.isPending}
            error={importMutation.error?.message ?? null}
            onBack={handleBack}
            onImport={handleImport}
          />
        ) : null}

        {step === "started" ? (
          <>
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success/15">
                <CheckCircle2 className="h-5 w-5 text-success" aria-hidden />
              </span>
              <div className="flex flex-col gap-1">
                <DialogTitle className="text-[17px] leading-tight">
                  <Trans>Import avviato</Trans>
                </DialogTitle>
                <DialogDescription className="text-[13px] text-ink-2">
                  <Trans>Il dizionario è stato accettato e viene applicato in background.</Trans>
                </DialogDescription>
              </div>
            </div>
            <p role="status" aria-live="polite" className="text-[12.5px] leading-relaxed text-ink-1">
              <Trans>
                Import avviato. Lo schema si aggiorna in background; segui l'avanzamento nella
                scheda Schema.
              </Trans>
            </p>
            <div className="mt-1 flex justify-end">
              <Button type="button" variant="primary" size="sm" onClick={() => onOpenChange(false)}>
                <Trans>Chiudi</Trans>
              </Button>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
ImportDictionaryDialog.displayName = "ImportDictionaryDialog";

// ── Preview step ────────────────────────────────────────────────────────────

interface PreviewStepProps {
  readonly preview: ImportDictionaryPreview;
  readonly fileName: string;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onBack: () => void;
  readonly onImport: () => void;
}

const PreviewStep: React.FC<PreviewStepProps> = ({
  preview,
  fileName,
  pending,
  error,
  onBack,
  onImport,
}) => {
  const tablesPct = pct(preview.matched_tables, preview.discovered_objects);
  const columnsPct = pct(preview.documented_columns, preview.total_columns_in_matched);
  const unmatchedCount = Math.max(0, preview.discovered_objects - preview.matched_tables);
  const unmatchedShown = preview.unmatched_objects.length;
  const undocumentedTop = preview.undocumented_top.slice(0, UNDOCUMENTED_TOP_N);

  return (
    <>
      <div className="flex flex-col gap-1">
        <DialogTitle className="text-[17px] leading-tight">
          <Trans>Anteprima copertura</Trans>
        </DialogTitle>
        <DialogDescription className="text-[13px] text-ink-2">
          <Trans>Nessuna modifica è stata ancora scritta. File: {fileName}</Trans>
        </DialogDescription>
      </div>

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-md border border-hairline bg-white/50 px-4 py-3 text-[12.5px]">
        <dt className="text-ink-2">
          <Trans>Tabelle nel dizionario</Trans>
        </dt>
        <dd className="text-right font-medium text-ink-1 tabular-nums" data-testid="stat-dictionary">
          {preview.dictionary_tables}
          <span className="ml-1 font-normal text-ink-3">
            <Trans>· {preview.dictionary_columns} colonne</Trans>
          </span>
        </dd>

        <dt className="text-ink-2">
          <Trans>Oggetti trovati nel database</Trans>
        </dt>
        <dd className="text-right font-medium text-ink-1 tabular-nums" data-testid="stat-discovered">
          {preview.discovered_objects}
        </dd>

        <dt className="text-ink-2">
          <Trans>Prefisso rilevato</Trans>
        </dt>
        <dd className="text-right font-medium text-ink-1" data-testid="stat-prefix">
          {preview.prefix ? (
            <code className="rounded bg-ink-3/10 px-1 py-0.5 font-mono text-[11.5px]">
              {preview.prefix}
            </code>
          ) : (
            <span className="text-ink-3">—</span>
          )}
        </dd>

        <dt className="text-ink-2">
          <Trans>Tabelle coperte</Trans>
        </dt>
        <dd className="text-right font-medium text-ink-1 tabular-nums" data-testid="stat-tables">
          {preview.matched_tables}/{preview.discovered_objects}
          <span className="ml-1 font-normal text-ink-3">({tablesPct}%)</span>
        </dd>

        <dt className="text-ink-2">
          <Trans>Colonne descritte</Trans>
        </dt>
        <dd className="text-right font-medium text-ink-1 tabular-nums" data-testid="stat-columns">
          {preview.documented_columns}/{preview.total_columns_in_matched}
          <span className="ml-1 font-normal text-ink-3">({columnsPct}%)</span>
        </dd>
      </dl>

      {unmatchedShown > 0 ? (
        <details className="rounded-md border border-hairline bg-white/40 text-[12px]">
          <summary className="cursor-pointer select-none px-3 py-2 font-medium text-ink-1">
            <Trans>Oggetti non coperti ({unmatchedCount})</Trans>
            {unmatchedShown < unmatchedCount ? (
              <span className="ml-1 font-normal text-ink-3">
                <Trans>— mostrati i primi {unmatchedShown}</Trans>
              </span>
            ) : null}
          </summary>
          <ul className="max-h-40 overflow-y-auto border-t border-hairline px-3 py-2 font-mono text-[11.5px] text-ink-2">
            {preview.unmatched_objects.map((name) => (
              <li key={name} className="truncate py-0.5">
                {name}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {undocumentedTop.length > 0 ? (
        <details className="rounded-md border border-hairline bg-white/40 text-[12px]">
          <summary className="cursor-pointer select-none px-3 py-2 font-medium text-ink-1">
            <Trans>Tabelle con più colonne senza descrizione</Trans>
          </summary>
          <ul className="border-t border-hairline px-3 py-2 text-[11.5px] text-ink-2">
            {undocumentedTop.map((row) => (
              <li key={row.object_name} className="flex items-center justify-between gap-3 py-0.5">
                <span className="truncate font-mono">{row.object_name}</span>
                <span className="shrink-0 tabular-nums">
                  <Trans>{row.missing} senza descrizione</Trans>
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      <div className="flex items-start gap-2 rounded-md border border-hairline bg-white/50 px-3 py-2 text-[12.5px] text-ink-2">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
        <span>
          <Trans>
            Le descrizioni importate vengono marcate come modificate a mano: la profilazione AI
            non le sovrascrive.
          </Trans>
        </span>
      </div>

      <InlineErrorBanner message={error} className="mt-0" />

      <div className="mt-1 flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onBack} disabled={pending}>
          <Trans>Indietro</Trans>
        </Button>
        <Button type="button" variant="primary" size="sm" onClick={onImport} disabled={pending}>
          {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
          <Trans>Importa</Trans>
        </Button>
      </div>
    </>
  );
};
PreviewStep.displayName = "ImportDictionaryPreviewStep";
